import { ExternalLink, Info, RadioTower, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { transferCandidates, sourceMethodology } from '../data/seed';
import type { TransferCandidate } from '../types';
import { SignalRing } from './SignalRing';

function Sleeve({ candidate, score, onSelect }: { candidate: TransferCandidate; score: number; onSelect: () => void }) {
  return (
    <button className="sleeve-card" onClick={onSelect} aria-label={`Open evidence for ${candidate.name}`}>
      <div className={`album-cover ${candidate.cover}`}>
        <div className="cover-score">{score}</div>
        <div className="cover-copy">
          <span>{candidate.role}</span>
          <strong>{candidate.name}</strong>
          <small>{candidate.club}</small>
        </div>
      </div>
      <div className="sleeve-meta">
        <div><strong>{candidate.name}</strong><span>{candidate.status}</span></div>
        <SignalRing score={score} />
      </div>
    </button>
  );
}

type SourceCheck = { sourceId: string; candidateId: string; url: string; httpStatus: number; mentionFound: boolean; checkedAt: string };

export function TransferShelf() {
  const [selectedId, setSelectedId] = useState(transferCandidates[0].id);
  const [checks, setChecks] = useState<SourceCheck[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void fetch('/api/signals').then(response => response.ok ? response.json() : Promise.reject()).then((data: { checks: SourceCheck[] }) => setChecks(data.checks)).catch(() => undefined);
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const response = await fetch('/api/signals/refresh', { method: 'POST' });
      if (response.ok) {
        const data = await response.json() as { checks: SourceCheck[] };
        setChecks(data.checks);
      }
    } finally {
      setRefreshing(false);
    }
  };

  const selected = transferCandidates.find(candidate => candidate.id === selectedId) || transferCandidates[0];
  const scoreFor = (candidate: TransferCandidate) => {
    const candidateChecks = checks.filter(check => check.candidateId === candidate.id);
    if (!candidateChecks.length) return candidate.score;
    const failures = candidateChecks.filter(check => check.httpStatus < 200 || check.httpStatus >= 400 || !check.mentionFound).length;
    return Math.max(0, candidate.score - failures * 6);
  };
  const selectedChecks = useMemo(() => checks.filter(check => check.candidateId === selected.id), [checks, selected.id]);
  const selectedScore = scoreFor(selected);
  const sourcePenalty = selected.score - selectedScore;
  const verifiedCount = checks.filter(check => check.httpStatus >= 200 && check.httpStatus < 400 && check.mentionFound).length;
  return (
    <section className="section-shell transfer-shell" id="transfers">
      <div className="section-heading">
        <div>
          <p className="section-kicker"><RadioTower size={15} /> Evidence-weighted transfer board</p>
          <h2>The transfer record shelf</h2>
          <p>Each sleeve is a live dossier. The 0–100 index rewards reputable reporting and deal specificity, then subtracts market friction.</p>
        </div>
        <div className="freshness source-controls"><span className="live-dot" /><span>{checks.length ? `${verifiedCount}/${checks.length} sources responding` : 'Snapshot verified 26 Jul 2026'}</span><button onClick={refresh} disabled={refreshing} aria-label="Refresh trusted source checks"><RefreshCw size={14} className={refreshing ? 'spin' : ''}/></button></div>
      </div>

      <div className="transfer-layout">
        <div className="shelf-scroll" aria-label="Transfer candidates">
          {transferCandidates.map(candidate => <Sleeve key={candidate.id} candidate={candidate} score={scoreFor(candidate)} onSelect={() => setSelectedId(candidate.id)} />)}
        </div>

        <aside className="evidence-panel">
          <div className="evidence-top">
            <div>
              <span className="evidence-band">{selected.band}</span>
              <h3>{selected.name}</h3>
              <p>{selected.club} · {selected.role}</p>
            </div>
            <SignalRing score={selectedScore} size="large" />
          </div>
          <p className="evidence-summary">{selected.summary}</p>
          {checks.length > 0 && <div className={`live-score-note ${sourcePenalty ? 'degraded' : 'healthy'}`}><strong>Internet source health</strong><span>{sourcePenalty ? `-${sourcePenalty} points while ${selectedChecks.filter(check => !check.mentionFound || check.httpStatus < 200 || check.httpStatus >= 400).length} cited source check fails` : 'All cited pages reachable and candidate terms confirmed'}</span></div>}
          <div className="signal-tracklist">
            {selected.signals.map(signal => (
              <div className="signal-row" key={signal.label}>
                <span>{signal.label}</span>
                <div className="signal-bar"><i style={{ width: `${Math.min(100, Math.abs(signal.value) * 3.4)}%` }} className={signal.value < 0 ? 'negative' : ''} /></div>
                <strong>{signal.value > 0 ? '+' : ''}{signal.value}</strong>
                <small>{signal.note}</small>
              </div>
            ))}
          </div>
          <div className="friction-note"><Info size={16} /><span><strong>Friction:</strong> {selected.friction}</span></div>
          <div className="source-stack">
            {selected.sources.map(source => {
              const check = selectedChecks.find(item => item.url === source.url);
              return (
              <a key={source.url} href={source.url} target="_blank" rel="noreferrer">
                <span><strong>{source.publisher} {check && <i className={check.mentionFound && check.httpStatus < 400 ? 'source-ok' : 'source-bad'} />}</strong><small>{source.published} · {source.detail}{check ? ` · checked ${new Date(check.checkedAt).toLocaleString()}` : ''}</small></span><ExternalLink size={16} />
              </a>
            )})}
          </div>
        </aside>
      </div>

      <details className="methodology">
        <summary>How the score is produced</summary>
        <ul>{sourceMethodology.map(item => <li key={item}>{item}</li>)}</ul>
      </details>
    </section>
  );
}
